import { useMutation } from "@tanstack/react-query";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { ProjectTemplate } from "PosterEditor/model/project-template";

export function useTemplateRemoteStore() {
  const { client, userID } = useAuthContext();

  const save = useMutation({
    mutationFn: async (template: ProjectTemplate) => {
      if (!client) {
        throw new Error("Client not initialized");
      }

      if (!userID)
        throw new Error("useTemplateRemoteStore 14: User not authenticated");
      const {
        id,
        name,
        thumbnail,
        createdAt,
        updatedAt,
        description,
        appliedElements,
        editingPermissions,
      } = template;
      try {
        const { data, error } = await client.from("templates").upsert(
          [
            {
              id,
              user_id: userID,
              title: name,
              created_at: new Date(createdAt).toISOString(),
              updated_at: new Date(updatedAt).toISOString(),
              thumbnail: thumbnail,
              description: description,
              applied_elements: appliedElements,
              project: template.project,
              editing_permissions: editingPermissions,
            },
          ],
          { onConflict: "id" }
        );

        if (error) {
          throw error;
        }

        return data;
      } catch (err) {
        throw err;
      }
    },
  });

  const remove = useMutation({
    mutationFn: async (templateID: string) => {
      if (!client) {
        throw new Error("Client not initialized");
      }
      if (!userID)
        throw new Error("useTemplateRemoteStore 62: User not authenticated");
      const { data, error } = await client
        .from("templates")
        .delete()
        .eq("id", templateID);
      if (error) throw error;
      return data;
    },
  });

  const fetchAllTemplates = async (): Promise<ProjectTemplate[]> => {
    if (!client) {
      throw new Error("Client not initialized");
    }

    const { data, error } = await client
      .from("templates")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    const templates: ProjectTemplate[] = data.map((template) => ({
      id: template.id,
      name: template.title,
      description: template.description,
      createdAt: new Date(template.created_at),
      updatedAt: new Date(template.updated_at),
      thumbnail: template.thumbnail,
      modelVersion: 1,
      appliedElements: template.applied_elements,
      project: { ...template.project },
      editingPermissions: template.editing_permissions,
    }));

    return templates;
  };

  const fetchTemplatesWithIDs = async (
    ids: string[]
  ): Promise<ProjectTemplate[]> => {
    if (!client) {
      throw new Error("Client not initialized");
    }

    const { data, error } = await client
      .from("templates")
      .select("*")
      .in("id", ids);

    if (error) throw error;

    const templates: ProjectTemplate[] = data.map((template) => ({
      id: template.id,
      name: template.title,
      description: template.description,
      createdAt: new Date(template.created_at),
      updatedAt: new Date(template.updated_at),
      thumbnail: template.thumbnail,
      modelVersion: 1,
      appliedElements: template.applied_elements,
      project: { ...template.project },
      editingPermissions: template.editing_permissions,
    }));

    return templates;
  };

  return { save, remove, fetchAllTemplates, fetchTemplatesWithIDs };
}
